import React, { useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { EntityType, PaymentMethodTypesEnum, PaymentService } from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import PayableCard from "../../../newComponents/listings/listing-cards/payables";
import SingleDropdown from "../../../newComponents/singleDropdown/SingleDropdown";
import SimpleButton from "../../../newComponents/buttons/Simple-button";
import RightSideModal from "../../../newComponents/Modal/right-side-modal";
import EmptyState from "../../../newComponents/emptystate";
import { GetImages } from "../../../utils/GetImages";
import ManageAutoPayForm from "./ManageAutoPayForm";

const PLAID_TOOLTIP = "At least one account must be connected to Plaid to enable Auto Pay";

const ChevronIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginLeft: "6px" }}
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="#64748B"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export interface TenantPaymentsTabContentProps {
  filteredRows: any[];
  loading: boolean;
  fetchData: (page?: number) => void;
  sentryRef: (node?: Element | null) => void;
  selectedFilter: string;
  menuItems: any[];
  tenantId?: number;
  isContractor?: boolean;
}

const TenantPaymentsTabContent: React.FC<TenantPaymentsTabContentProps> = ({
  filteredRows,
  loading,
  fetchData,
  sentryRef,
  selectedFilter,
  menuItems,
  tenantId,
  isContractor,
}) => {
  const [isAutoPayOpen, setIsAutoPayOpen] = useState(false);
  const [plaidAccounts, setPlaidAccounts] = useState<{ Id: number; Name: string }[]>([]);
  const [existingAutoPaySetting, setExistingAutoPaySetting] = useState<any>(null);

  const autoPayFormId = "tenant-manage-auto-pay-form";
  const autoPayFormMethods = useForm({
    mode: "onChange",
    defaultValues: {
      isEnabled: "true",
      plaidAccountId: "",
    },
  });

  useEffect(() => {
    if (!tenantId || isContractor) return;

    apiHelper(
      PaymentService.getApiPaymentMethodTenantlist,
      { showNotification: false },
      tenantId
    )
      .then((res) => {
        const raw: any[] = res?.data?.success || [];
        const filtered = raw.filter(
          (item) => item.paymentMethodTypeId === PaymentMethodTypesEnum.Plaid
        );
        setPlaidAccounts(
          filtered.map((item) => ({
            Id: item.id,
            Name: item.accountName || item.bankName || `Account #${item.id}`,
          }))
        );
      })
      .catch(() => setPlaidAccounts([]));

    apiHelper(
      PaymentService.getApiAutoPaySettingTenantlist,
      { showNotification: false },
      tenantId
    )
      .then((res) => {
        const items: any[] = res?.data?.success || [];
        setExistingAutoPaySetting(items[0] ?? null);
      })
      .catch(() => setExistingAutoPaySetting(null));
  }, [tenantId, isContractor]);

  const openAutoPayDrawer = () => {
    autoPayFormMethods.reset({
      isEnabled: existingAutoPaySetting?.isEnabled === false ? "false" : "true",
      plaidAccountId: existingAutoPaySetting?.plaidAccountId
        ? String(existingAutoPaySetting.plaidAccountId)
        : "",
    });
    setIsAutoPayOpen(true);
  };

  const handleSaveAutoPay = async (values: any) => {
    try {
      const payload: any = {
        entityId: tenantId,
        entityTypeId: EntityType.Tenant,
        isEnabled: values.isEnabled === "true",
        plaidAccountId: values.plaidAccountId ? Number(values.plaidAccountId) : undefined,
      };

      if (existingAutoPaySetting?.id) {
        payload.id = existingAutoPaySetting.id;
        const res = await apiHelper(
          PaymentService.putApiAutoPaySetting,
          {
            title: "Auto Pay Updated",
            successMessage: "Auto Pay settings updated successfully",
            failureMessage: "Unable to update Auto Pay settings",
            showNotification: true,
          },
          payload
        );
        setExistingAutoPaySetting(res?.data?.success ?? payload);
      } else {
        const res = await apiHelper(
          PaymentService.postApiAutoPaySetting,
          {
            title: "Auto Pay Enabled",
            successMessage: "Auto Pay has been enabled",
            failureMessage: "Unable to enable Auto Pay",
            showNotification: true,
          },
          payload
        );
        setExistingAutoPaySetting(res?.data?.success ?? null);
      }

      setIsAutoPayOpen(false);
    } catch (err) {
      console.error("Manage Auto Pay error", err);
    }
  };

  const hasPlaidAccounts = plaidAccounts.length > 0;

  return (
    <div className="payment-tab-wrapper">
      <div className="payment-tab-heading">
        <h2 className="black">Payment</h2>
        <div className="payment-tab-heading__actions">
          <SingleDropdown
            triggerText={selectedFilter}
            menuItems={menuItems}
            triggerIconPosition="right"
            triggerIcon={<ChevronIcon />}
          />
          {!isContractor && (
            <span
              title={!hasPlaidAccounts ? PLAID_TOOLTIP : undefined}
              style={{ display: "inline-block" }}
            >
              <SimpleButton
                label="Manage Auto Pay"
                onClick={openAutoPayDrawer}
                isDisabled={!hasPlaidAccounts}
              />
            </span>
          )}
        </div>
      </div>

      <div className="tenant-payment-list">
        {filteredRows.length === 0 && !loading ? (
          <div className="tenant-empty-filter-state">
            {selectedFilter !== "All Payments" ? (
              <EmptyState
                icon={GetImages.emptyStatePayment}
                heading="No Payment Found with Applied Filters"
                isButton={false}
                className="empty-state-wrapper"
              />
            ) : (
              <EmptyState
                icon={GetImages.emptyStatePayment}
                className="bg-transparent"
                isBorder={false}
                heading="No Payments Available"
                isButton={false}
              />
            )}
          </div>
        ) : (
          <div className="tanant-payment-card-list">
            <PayableCard
              id={tenantId || 0}
              type="tenant"
              className="tenant-dashboard-payable-card"
              isFooter={false}
              dataHookState={{ rows: filteredRows, loading, fetchData }}
            />
            <div ref={sentryRef} />
          </div>
        )}
      </div>

      <RightSideModal
        isOpen={isAutoPayOpen}
        onClose={() => setIsAutoPayOpen(false)}
        title="Manage Auto Pay"
        buttonlabel="Save"
        formId={autoPayFormId}
        maxWidth={480}
        isDirty={autoPayFormMethods.formState.isDirty}
        isSubmitDisabled={autoPayFormMethods.formState.isSubmitting || !autoPayFormMethods.formState.isDirty || !autoPayFormMethods.formState.isValid}
      >
        <FormProvider {...autoPayFormMethods}>
          <ManageAutoPayForm
            formId={autoPayFormId}
            onSubmit={handleSaveAutoPay}
            accountOptions={plaidAccounts}
            infoText="Auto payment of rent will start from the next payment due date"
          />
        </FormProvider>
      </RightSideModal>
    </div>
  );
};

export default TenantPaymentsTabContent;
